import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCookieConsent } from '@/hooks/use-cookie-consent';
import { useCookieDialogOpen } from '@/hooks/use-cookie-dialog-open';
import { XIcon } from 'lucide-react';

import { UNCONFIRMED_CLIENT_ROLE } from '@/features/auth/hooks/use-is-unconfirmed-client';
import { useWhoami } from '@/features/auth/hooks/use-whoami-query';
import { useLogConsent } from '@/features/user/hooks/use-log-consent';

import { Typography } from '@/components/typography/typography';

import { MainButton } from '@/lib/shadcn/components/ui/main-button/main-button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/lib/shadcn/components/ui/sheet';
import { Stack } from '@/lib/shadcn/components/ui/stack/stack';
import { Switch } from '@/lib/shadcn/components/ui/switch';

export function CookieSettingsDrawer() {
  const { t } = useTranslation();
  const { preferences, savePreferences, rejectAll } = useCookieConsent();
  const { isOpen, setIsOpen, close } = useCookieDialogOpen();
  const { data: whoamiData } = useWhoami({ params: {} });
  const isAuthorized =
    whoamiData !== undefined &&
    whoamiData.auth.userId !== null &&
    !whoamiData.auth.roles.includes(UNCONFIRMED_CLIENT_ROLE);
  const { mutateAsync: logConsentAsync } = useLogConsent();

  const [analyticsEnabled, setAnalyticsEnabled] = useState(preferences.analytics);

  useEffect(() => {
    if (isOpen) {
      setAnalyticsEnabled(preferences.analytics);
    }
  }, [isOpen, preferences.analytics]);

  const handleSave = async () => {
    const wasAnalyticsEnabled = preferences.analytics;
    close();

    if (isAuthorized) {
      try {
        await logConsentAsync({
          action: analyticsEnabled ? 'grant' : 'revoke',
          formType: 'cookie',
          signedDocuments: ['metrics_consent', 'recommendation_rules'],
          checkboxesState: {
            metrics_consent: analyticsEnabled,
            recommendation_rules: analyticsEnabled,
          },
        });
      } catch (error) {
        console.error('Failed to log cookie consent', error);
      }
    }

    savePreferences(analyticsEnabled);

    if (
      wasAnalyticsEnabled &&
      !analyticsEnabled &&
      typeof globalThis !== 'undefined' &&
      globalThis.location
    ) {
      globalThis.location.reload();
    }
  };

  const handleReject = async () => {
    const wasAnalyticsEnabled = preferences.analytics;
    close();

    if (isAuthorized) {
      try {
        await logConsentAsync({
          action: 'revoke',
          formType: 'cookie',
          signedDocuments: ['metrics_consent', 'recommendation_rules'],
          checkboxesState: {
            metrics_consent: false,
            recommendation_rules: false,
          },
        });
      } catch (error) {
        console.error('Failed to log cookie rejection', error);
      }
    }

    rejectAll();

    if (wasAnalyticsEnabled && typeof globalThis !== 'undefined' && globalThis.location) {
      globalThis.location.reload();
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="bottom"
        className="fixed inset-x-0 bottom-0 z-[10000] mx-auto flex w-full max-w-lg flex-col gap-unit-5 rounded-t-large border-t border-slate-100 bg-white p-unit-6 pb-[calc(16px+env(safe-area-inset-bottom,0px))] shadow-2xl"
      >
        <SheetHeader className="flex flex-row items-center justify-between">
          <SheetTitle asChild>
            <Typography.h5 className="font-semibold text-slate-500">
              {t('cookie-dialog.menu-item')}
            </Typography.h5>
          </SheetTitle>
          <SheetClose
            aria-label={t('common.close', 'Закрыть')}
            className="flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-medium bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 active:bg-slate-250"
          >
            <XIcon className="size-5 text-slate-500" />
          </SheetClose>
        </SheetHeader>

        <SheetDescription className="sr-only">
          {t('cookie-dialog.menu-item')}
        </SheetDescription>

        {/* Блок категорий куки с тумблерами */}
        <Stack gap={4} className="rounded-large bg-slate-50 p-unit-5">
          {/* 1. Технические (Обязательные) */}
          <Stack
            direction="row"
            align="center"
            justify="between"
            className="w-full gap-unit-3"
          >
            <Stack gap={1} className="flex-1">
              <Typography.p4 className="font-semibold text-slate-500">
                {t('cookie-dialog.categories.technical.title')}
              </Typography.p4>
              <Typography.small1 className="leading-normal text-slate-500">
                {t('cookie-dialog.categories.technical.description')}
              </Typography.small1>
            </Stack>
            <Switch checked disabled />
          </Stack>

          {/* 2. Аналитические (Опциональные) */}
          <Stack
            direction="row"
            align="center"
            justify="between"
            className="w-full gap-unit-3 pt-unit-2"
          >
            <Stack gap={1} className="flex-1">
              <Typography.p4 className="font-semibold text-slate-500">
                {t('cookie-dialog.categories.analytics.title')}
              </Typography.p4>
              <Typography.small1 className="leading-normal text-slate-500">
                {t('cookie-dialog.categories.analytics.description')}
              </Typography.small1>
            </Stack>
            <Switch checked={analyticsEnabled} onCheckedChange={setAnalyticsEnabled} />
          </Stack>
        </Stack>

        {/* Акцентные тиловые кнопки TMG */}
        <Stack direction="row" gap={3} className="w-full pt-unit-2">
          <MainButton
            size="s"
            variant="outlined"
            onClick={handleReject}
            className="flex-1"
            data-testid="cookie-drawer-reject-button"
          >
            {t('cookie-dialog.reject-all')}
          </MainButton>

          <MainButton
            size="s"
            variant="filled"
            onClick={handleSave}
            className="flex-1"
            data-testid="cookie-drawer-save-button"
          >
            {t('cookie-dialog.save-settings')}
          </MainButton>
        </Stack>
      </SheetContent>
    </Sheet>
  );
}
